/**
 * Turns the flavor cards into a product swap instead of a navigation.
 *
 * product-info.js already knows how to replace <main> with another product's page and
 * rewrite the URL -- it does that whenever an option value change carries a different
 * data-product-url. Rather than re-implementing the fetch, this republishes exactly the
 * payload its subscriber expects, so the swap, the <title> update, the history entry and
 * the re-initialised sliders all come from the theme's own code path.
 */
// A product swap re-creates the <script> tags inside <main>, so this file runs again
// on every swap. Defining the element twice throws, so guard it the way product-info.js does.
if (!customElements.get('alt-variant-selector')) {

class AltVariantSelector extends HTMLElement {
    connectedCallback() {
        this.productInfo = this.closest('product-info');

        this.controller?.abort();
        this.controller = new AbortController();

        this.addEventListener('click', this.onClick.bind(this), { signal: this.controller.signal });

        this.restoreScroll();
    }

    disconnectedCallback() {
        this.controller?.abort();
    }

    onClick(event) {
        const card = event.target.closest('.alt-variant-card');
        if (!card || !this.contains(card)) return;

        if (card.classList.contains('active')) {
            event.preventDefault();
            return;
        }

        // Let the browser handle anything that is not a plain left click, and fall back to
        // a normal navigation when the swap machinery is not available.
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (!this.productInfo || typeof publish !== 'function') return;

        event.preventDefault();
        this.setLoading(card);
        this.rememberScroll();

        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
                event,
                target: card,
                selectedOptionValues: [],
            },
        });
    }

    // The switcher in the info column is the anchor even when the tap came from the copy in
    // the sticky bar: the bar is fixed, so its own position says nothing about the page.
    static mainSelector() {
        return Array.from(document.querySelectorAll('alt-variant-selector'))
            .find((element) => !element.closest('sticky-atc'));
    }

    rememberScroll() {
        const anchor = AltVariantSelector.mainSelector()?.getBoundingClientRect().top;
        if (anchor !== undefined) window.altVariantScrollAnchor = anchor;
    }

    /**
     * product-info.js replaces <main> wholesale and then focuses the clicked card. In
     * between, the fresh markup is briefly shorter than what it replaced, so the browser
     * clamps the scroll position to the bottom of the document -- the page appears at the
     * footer -- and the focus call then yanks it back up.
     *
     * Putting the switcher back under the same point of the viewport, inside a frame so it
     * lands before the next paint, removes both halves of that.
     */
    restoreScroll() {
        const anchor = window.altVariantScrollAnchor;
        if (anchor === undefined || this.closest('sticky-atc')) return;

        delete window.altVariantScrollAnchor;

        // A single correction is not enough. Right after the swap the gallery images have no
        // dimensions yet, so the document is far shorter than it will be; the position we
        // want may simply not exist to scroll to, and the browser clamps to the bottom --
        // which is the footer the visitor sees. The correction is therefore re-applied every
        // frame while the layout settles, and stops early if they start scrolling themselves.
        // The theme sets html { scroll-behavior: smooth }, so every programmatic scroll is
        // animated -- including the one product-info.js causes by focusing the clicked card.
        // That animation is the visible ride from the footer up to the section. Suspended
        // here, synchronously, because focus() runs in a microtask right after this and
        // would otherwise start animating before the first frame.
        const root = document.documentElement;
        const previousBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';

        const deadline = performance.now() + 600;
        let cancelled = false;
        const cancel = () => { cancelled = true; };

        const finish = () => {
            root.style.scrollBehavior = previousBehavior;
            window.removeEventListener('wheel', cancel);
            window.removeEventListener('touchstart', cancel);
        };

        window.addEventListener('wheel', cancel, { once: true, passive: true });
        window.addEventListener('touchstart', cancel, { once: true, passive: true });

        const step = () => {
            if (cancelled) {
                finish();
                return;
            }

            const delta = this.getBoundingClientRect().top - anchor;
            if (Math.abs(delta) > 1) window.scrollBy(0, delta);

            if (performance.now() < deadline) {
                requestAnimationFrame(step);
            } else {
                finish();
            }
        };

        requestAnimationFrame(step);
    }

    setLoading(card) {
        this.setAttribute('aria-busy', 'true');
        this.querySelectorAll('.alt-variant-card').forEach((item) => {
            item.classList.toggle('is-pending', item === card);
        });
    }
}

customElements.define('alt-variant-selector', AltVariantSelector);

}
